// components/IlustrationsGallery.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import ArtistInfo from "./ArtistInfo";
import "./Ilustrations.css";
import { useModal } from "../../contexts/ModalContext";
import { useSearchParams } from "react-router-dom";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import { illustrations, Illustration } from "../../data/illustrations";

// Remove acentos, normaliza hífens/espaços e põe em minúsculas
const normalizeText = (str: string): string =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-\s]+/g, " ") // hífens e espaços múltiplos viram um espaço
    .trim()
    .toLowerCase();

const IlustrationsGallery: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<Illustration | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { setIsModalOpen } = useModal();
  const modalTextRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Busca por nome comum, científico, nome em inglês ou artista
  const filteredItems = useMemo(() => {
    const term = normalizeText(searchTerm);
    if (!term) return illustrations;

    return illustrations.filter(({ name, bird, artist }) =>
      [
        name,
        bird.scientificName,
        bird.englishName,
        artist?.name,
        artist?.fullName,
      ].some((field) => field && normalizeText(field).includes(term))
    );
  }, [searchTerm]);

  // Initialize viewport height CSS custom property
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
  }, []);

  // Prevent body scroll when modal is open and handle mobile viewport
  useEffect(() => {
    if (selectedItem) {
      // Prevent body scroll
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      // Force mobile browsers to hide UI elements
      if (window.innerHeight < window.outerHeight) {
        // Mobile browser detected, add viewport-fit and other mobile-specific fixes
        document.body.style.height = "100vh";
        document.body.style.height = "100dvh";
        document.documentElement.style.height = "100vh";
        document.documentElement.style.height = "100dvh";
      }

      // Add meta viewport if not present (for better mobile handling)
      let metaViewport = document.querySelector('meta[name="viewport"]');
      if (!metaViewport) {
        metaViewport = document.createElement("meta");
        metaViewport.setAttribute("name", "viewport");
        document.head.appendChild(metaViewport);
      }
      const originalContent = metaViewport.getAttribute("content");
      metaViewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=no"
      );

      setIsModalOpen(true);

      // Cleanup function
      return () => {
        document.body.style.overflow = "unset";
        document.documentElement.style.overflow = "unset";
        document.body.style.height = "unset";
        document.documentElement.style.height = "unset";

        // Restore original viewport content
        if (metaViewport && originalContent) {
          metaViewport.setAttribute("content", originalContent);
        }

        setIsModalOpen(false);
      };
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
      document.body.style.height = "unset";
      document.documentElement.style.height = "unset";
      setIsModalOpen(false);
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
      document.body.style.height = "unset";
      document.documentElement.style.height = "unset";
      setIsModalOpen(false);
    };
  }, [selectedItem, setIsModalOpen]);

  // Abre o modal a partir de /ilustracoes?bird=<id da ave em birds.ts>[&img=<posição>]
  useEffect(() => {
    const birdId = searchParams.get("bird");
    if (birdId) {
      const id = parseInt(birdId, 10);
      const index = parseInt(searchParams.get("img") ?? "0", 10);
      const item =
        illustrations.find((i) => i.bird.id === id && i.index === index) ??
        illustrations.find((i) => i.bird.id === id);
      if (item) {
        // Limpa a busca ao abrir por parâmetro, para a navegação do modal funcionar
        setSearchTerm("");
        setSelectedItem(item);
        // Remove o parâmetro depois de abrir o modal
        setSearchParams({});
      }
    } else {
      // Só rola ao topo se não for abrir um modal
      window.scrollTo(0, 0);
    }
  }, [searchParams, setSearchParams]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  // Preload adjacent images for smoother navigation
  const preloadImage = (src: string) => {
    const img = new Image();
    img.src = src;
  };

  const preloadAdjacentImages = (current: Illustration) => {
    const currentIndex = filteredItems.findIndex((i) => i.src === current.src);
    const nextIndex = (currentIndex + 1) % filteredItems.length;
    const prevIndex =
      (currentIndex - 1 + filteredItems.length) % filteredItems.length;

    // Preload next and previous images
    if (filteredItems[nextIndex]) preloadImage(filteredItems[nextIndex].src);
    if (filteredItems[prevIndex]) preloadImage(filteredItems[prevIndex].src);
  };

  // Store the viewport height handler for cleanup
  const viewportHandler = useRef<(() => void) | null>(null);

  const openModal = (item: Illustration) => {
    setSelectedItem(item);
    preloadAdjacentImages(item);

    // Set CSS custom property for actual viewport height
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    viewportHandler.current = setVH;
    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);
  };

  const closeModal = () => {
    setSelectedItem(null);

    // Clean up event listeners
    if (viewportHandler.current) {
      window.removeEventListener("resize", viewportHandler.current);
      window.removeEventListener("orientationchange", viewportHandler.current);
      viewportHandler.current = null;
    }
  };

  const nextItem = () => {
    if (!selectedItem) return;
    const currentIndex = filteredItems.findIndex(
      (i) => i.src === selectedItem.src
    );
    const nextIndex = (currentIndex + 1) % filteredItems.length;
    const newItem = filteredItems[nextIndex];
    setSelectedItem(newItem);
    preloadAdjacentImages(newItem);

    // Reset scroll position
    if (modalTextRef.current) {
      modalTextRef.current.scrollTop = 0;
    }
  };

  const prevItem = () => {
    if (!selectedItem) return;
    const currentIndex = filteredItems.findIndex(
      (i) => i.src === selectedItem.src
    );
    const prevIndex =
      (currentIndex - 1 + filteredItems.length) % filteredItems.length;
    const newItem = filteredItems[prevIndex];
    setSelectedItem(newItem);
    preloadAdjacentImages(newItem);

    // Reset scroll position
    if (modalTextRef.current) {
      modalTextRef.current.scrollTop = 0;
    }
  };

  return (
    <div className="ilustrations">
      <div className="gallery-container">
        <h1 className="gallery-title">Galeria de Ilustrações</h1>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Buscar por nome comum, científico ou artista..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            {searchTerm && (
              <button onClick={clearSearch} className="search-clear">
                ×
              </button>
            )}
          </div>
          <p className="search-results">
            {
              searchTerm
                ? `${filteredItems.length} ilustraç${
                    filteredItems.length !== 1 ? "ões" : "ão"
                  } encontrada${filteredItems.length !== 1 ? "s" : ""}`
                : "\u00A0" // Non-breaking space to maintain height
            }
          </p>
        </div>

        <div className="ilustrations-grid">
          {Array.from({ length: 4 }).map((_, columnIndex) => (
            <div className="ilustrations-column" key={columnIndex}>
              {filteredItems
                .filter((_, index) => index % 4 === columnIndex)
                .map((item) => (
                  <div
                    key={item.src}
                    className="ilustrations-card"
                    onClick={() => openModal(item)}
                  >
                    <div className="ilustrations-image-container">
                      <img
                        src={item.src}
                        alt={item.name}
                        className="ilustrations-image"
                        loading="lazy"
                        decoding="async"
                      />

                      <div className="ilustrations-overlay">
                        <h3 className="ilustrations-name">{item.name}</h3>

                        <p className="ilustrations-scientific">
                          ({item.bird.scientificName})
                        </p>

                        {item.artist && (
                          <ArtistInfo
                            artist={item.artist}
                            className="card-artist-info"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>

            <div className="modal-body">
              <div className="modal-image-container">
                <img
                  src={selectedItem.src}
                  alt={selectedItem.name}
                  className="modal-image"
                />
              </div>

              <div className="modal-info">
                <div className="modal-text-content" ref={modalTextRef}>
                  <h2 className="modal-title">{selectedItem.name}</h2>
                  <h3 className="modal-subtitle">
                    ({selectedItem.bird.scientificName})
                  </h3>

                  {selectedItem.artist && (
                    <ArtistInfo
                      artist={selectedItem.artist}
                      className="modal-artist-info"
                    />
                  )}

                  {selectedItem.bird.description && (
                    <p className="modal-description">
                      {selectedItem.bird.description}
                    </p>
                  )}
                </div>

                {selectedItem.bird.audio && (
                  <div className="modal-audio">
                    <span className="modal-audio-label"><i className="fi fi-bs-volume"></i> Ouvir canto</span>
                    <AudioPlayer src={selectedItem.bird.audio} />
                  </div>
                )}

                <div className="modal-navigation">
                  <button className="nav-button nav-prev" onClick={prevItem}>
                    <i className="fi fi-br-angle-left"></i>
                    Anterior
                  </button>
                  <button className="nav-button nav-next" onClick={nextItem}>
                    Próximo
                    <i className="fi fi-br-angle-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IlustrationsGallery;
